<!--
// app/components/pages/region-world-cup/BracketWorldCupFirstEdition.vue
-->
<script setup lang="ts">
import type { BracketData } from "~/src/world-cup/domain/models/Bracket";
import type { Team } from "~/src/team/domain/models/Team";
import { useSeasons } from "~/app/composables/season/seasons";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import MatchItemBracketTablesWorldCup from "~/app/components/pages/region-world-cup/MatchItemBracketTablesWorldCup.vue";
import IconArrowBracketTablesWorldCup from "~/app/components/pages/region-world-cup/IconArrowBracketTablesWorldCup.vue";

type Props = {
  teamsObj: Record<string, Team>;
  bracketData: BracketData;
  bracketKey: number;
  isHomeComponent?: boolean;
  inOtherLeaguesHome?: boolean;
};

const props = defineProps<Props>();

const { getSeasonTurns, getSeasonTeams } = useSeasons();
const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { locale } = useI18n();

const seasonMatches = await getSeasonTurns(13);

const findMatchInSeason = (turnIndex: number, matchId: number | null) => {
  if (!seasonMatches || !matchId) return undefined;
  const seasonMatchesValues = Object.values(seasonMatches);
  return seasonMatchesValues[turnIndex].find(m => m.id === matchId);
};

const trophyImage = "/img/world-cup/kwc-trophy.png";
</script>

<template>
  <div
    v-if="bracketData && Object.keys(teamsObj).length"
    id="tablesSlider"
    :key="bracketKey"
    :class="[
      'w-full overflow-x-scroll overflow-y-hidden hide-scrollbar bigsm:px-4',
      { 'max-h-[633px]': isHomeComponent },
    ]"
  >
    <div
      :class="[
        'grid grid-flow-col auto-cols-max gap-20 w-auto',
        { 'bigsm:-ms-32 lg:-ms-48 -mt-16': isHomeComponent },
      ]"
      :style="isHomeComponent ? 'scale: 0.7' : 'scale: 0.8'"
    >
      <!--Redemption-->
      <div v-if="bracketData.redemption.length" class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.redemption") }}</h3>
        <div class="round-container kwc-border-color self-stretch">
          <match-item-bracket-tables-world-cup
            v-for="match in bracketData.redemption"
            :key="match.matchId"
            :match="findMatchInSeason(0, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            :external-link="inOtherLeaguesHome"
          ></match-item-bracket-tables-world-cup>
        </div>
      </div>

      <!--Round 1-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round1") }}</h3>
        <div class="round-container-cols-2 kwc-border-color">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.round1.slice(0, 8)"
              :key="match.matchId || i"
              :match="findMatchInSeason(1, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>
          </div>
          <div class="flex items-stretch justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.round1.slice(8, 16)"
              :key="match.matchId || i"
              :match="findMatchInSeason(1, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>
          </div>

          <icon-arrow-bracket-tables-world-cup
            v-if="bracketData.redemption.length"
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--Round 2-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round2") }}</h3>
        <div class="round-container-cols-2 kwc-border-color !mb-20 self-stretch">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.round2Winners.slice(0, 4)"
              :key="match.matchId || i"
              :match="findMatchInSeason(2, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>
          </div>
          <div class="flex items-stretch justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.round2Winners.slice(4, 8)"
              :key="match.matchId || i"
              :match="findMatchInSeason(2, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>
          </div>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-0"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-1"
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="p-[2px] linear-gradient-red self-stretch">
          <div class="round-container-cols-2 !border-none bg-black">
            <div class="flex flex-col items-stretch gap-4">
              <match-item-bracket-tables-world-cup
                v-for="(match, i) in bracketData.round2Losers.slice(0, 4)"
                :key="match.matchId || i"
                :match="findMatchInSeason(2, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
                :external-link="inOtherLeaguesHome"
              ></match-item-bracket-tables-world-cup>
            </div>
            <div class="flex items-stretch justify-center px-5">
              <div class="w-full"></div>
              <div class="w-[2px] kwc-divider"></div>
            </div>
            <div class="flex flex-col items-stretch gap-4">
              <match-item-bracket-tables-world-cup
                v-for="(match, i) in bracketData.round2Losers.slice(4, 8)"
                :key="match.matchId || i"
                :match="findMatchInSeason(2, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
                :external-link="inOtherLeaguesHome"
              ></match-item-bracket-tables-world-cup>
            </div>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="0-1"
              :right="locale === 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="1-1"
              :right="locale !== 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="bottom-0"
              text-indicator="0-2"
              :right="locale !== 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
      </div>

      <!--Last Chance-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.lastChance") }}</h3>
        <div class="p-[2px] linear-gradient-red my-auto">
          <div class="round-container !border-none bg-black">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.lastChance"
              :key="match.matchId || i"
              :match="findMatchInSeason(3, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              name-no-bold
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[66px]"
              bg-color="#7E0900"
              top-position-class="top-full"
              text-indicator="1-2"
              vertical
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
        <div
          class="self-stretch linear-gradient-red font-['Archivo_Expanded'] flex justify-center items-center p-2"
        >
          OUT
        </div>
      </div>

      <!--octavos-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.roundOf16") }}</h3>
        <div class="p-[2px] my-auto self-stretch">
          <div class="round-container !gap-8 kwc-border-color bg-black">
            <match-item-bracket-tables-world-cup
              v-for="(match, i) in bracketData.roundOf16"
              :key="match.matchId || i"
              :match="findMatchInSeason(4, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              name-no-bold
              :external-link="inOtherLeaguesHome"
            ></match-item-bracket-tables-world-cup>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[580px]"
              bg-color="#D4B78E"
              top-position-class="top-2"
              text-indicator="2-0"
              :right="locale === 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-3px)]"
              bg-color="#D4B78E"
              top-position-class="top-1/2"
              text-indicator="2-1"
              is-gradient-bg="linear-gradient(90deg, #5C010A 0%, #DBBE93 50%, #AE8957 100%)"
              :right="locale === 'ar'"
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
      </div>

      <!--cuartos-->
      <div class="round-col-container">
        <h3 :class="['round-title kwc-color max-w-32', { '!-mb-1': locale === 'en' }]">
          {{ $t("bracketKwc.turnsTitle.quarterFinals") }}
        </h3>
        <div class="round-container kwc-border-color">
          <match-item-bracket-tables-world-cup
            v-for="(match, i) in bracketData.quarterFinals"
            :key="match.matchId || i"
            :match="findMatchInSeason(5, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-3px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--semis-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.semifinals") }}</h3>
        <div class="round-container kwc-border-color">
          <match-item-bracket-tables-world-cup
            v-for="(match, i) in bracketData.semifinals"
            :key="match.matchId || i"
            :match="findMatchInSeason(6, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[85px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--final-->
      <div class="round-col-container active-stage">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.final") }}</h3>
        <div class="round-container kwc-border-color">
          <img
            :src="trophyImage"
            alt="Trophy KWC"
            width="180"
            height="200"
            class="absolute bottom-[91%] self-center"
          />
          <match-item-bracket-tables-world-cup
            v-for="(match, i) in bracketData.final"
            :key="match.matchId || i"
            :match="findMatchInSeason(6, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
            :external-link="inOtherLeaguesHome"
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[85px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.round-title {
  @apply font-['Termina'] font-semibold text-xl text-center uppercase mb-6 break-words;
}

.round-container {
  @apply border-2 px-5 py-7 flex flex-col items-stretch gap-4 my-auto relative bg-black;
}

.round-container-cols-2 {
  @apply border-2 px-5 py-7 gap-4 my-auto grid grid-cols-[1fr_auto_1fr] relative justify-items-stretch bg-black;
}

.round-col-container {
  @apply flex flex-col justify-stretch items-center;
}

.linear-gradient-red {
  background-image: linear-gradient(90deg, #54000a 0%, #b70007 32%, #d2283d 51%, #b70007 70%, #54000a 100%);
}

.kwc-divider {
  background-image: repeating-linear-gradient(
    175deg,
    #d4b78e,
    #d4b78e 12px,
    transparent 12px,
    transparent 24px,
    #d4b78e 22px
  );
}

</style>
